import { createStore, applyMiddleware, combineReducers } from 'redux';
import { Bean, Dices, Pointer, Scale, Crown, BadgeCheck, Trash2 } from 'lucide-react';

import SpillYourBeans from "../assets/beans.svg";
import MostLikely from "../assets/mostlikely.svg";
import PickASide from "../assets/pickaside.svg";
import RandomQuestions from "../assets/random.svg";

import AppConstants from '../constants/app-constants';
import Middleware from '../middlewares/app-middleware';
import AppUtils from '../utils/app-utils';

import English from '../languages/english';
//import Swedish from '../languages/swedish';

import { AppAction, LobbyState, SpeakerState, AuthState, UiState, PageState, SettingsState, RootState, OngoingGame } from '../types/store';

// Initial states
const initialAuth: AuthState = {
  login: {
    isLoading: false,
    error: null,
    loggedIn: false,
  },
  register: {
    isLoading: false,
    error: null,
    isRegistered: false,
  },
  update: {
    isLoading: false,
    error: null
  },
  user: {
    id: 0,
    email: '',
    nickname: '',
    motd: '',
    stats: []
  },
};

const initialUi: UiState = {
  ongoingGames: [],
  websocketState: AppConstants.STATE_WEBSOCKET_IDLE,
  websocketLastError: 0,
  language: English,
  languageAvailable: [English] //, Swedish]
};

const initialLobby: LobbyState = {
  isLoading: false,
  error: null,
  code: 0,
  nickname: '',
  players: [],
  state: AppConstants.STATE_DESYNC,
  timeleft: 0,
  number_questions: 0,
  question: 0,
  answer: 0,
  ready: 0,
  ready_status: '',
  categories: [],
  playing: false,
  admin: false,
  show_question: true,
  current_question: {
    category: 0,
    question: '',
    options: [],
  },
  drink: {
    question_extra: '',
    text: '',
    correct_option: '',
    correct_option_id: 0,
    correct: [],
    wrong: [],
    timeout: [],
  },
  streak: {
    players: []
  },
  advertise: {
    title: '',
    type: '',
    url: '',
  },
  done: {
    players: [],
  },
};

const initialSpeaker: SpeakerState = {
  isLoading: false,
  error: null,
  code: '',
  current: 0,
  questions: []
};

const initialPage: PageState = {
  join: {
    isLoading: false,
    error: null,
    code: 0,
    nickname: '',
  },
  create: {
    isLoading: false,
    error: null,
    success: false,
    mode: 0,
    code: 0,
  },
  lobby: initialLobby,
  speaker: initialSpeaker,
};

const initialSettings: SettingsState = {
  modes: [
    { id: AppConstants.GAMEMODE_STANDARD },
    { id: AppConstants.GAMEMODE_SPEAKER },
  ],
  categories: [
    { id: AppConstants.CATEGORY_SPILL_BEANS, icon: Bean, color: 'saddlebrown', background: SpillYourBeans, availableModes: [AppConstants.GAMEMODE_STANDARD] },
    { id: AppConstants.CATEGORY_PICK_SIDE, icon: Scale, color: 'orange', background: PickASide, availableModes: [AppConstants.GAMEMODE_STANDARD, AppConstants.GAMEMODE_SPEAKER] },
    { id: AppConstants.CATEGORY_MOST_LIKELY, icon: Pointer, color: 'green', background: MostLikely, availableModes: [AppConstants.GAMEMODE_STANDARD, AppConstants.GAMEMODE_SPEAKER] },
    { id: AppConstants.CATEGORY_RANDOM_QUESTIONS, icon: Dices, color: 'black', background: RandomQuestions, availableModes: [AppConstants.GAMEMODE_STANDARD] },
  ],
  languages: [
    { id: 1, language: "English", image: "https://cdn.moes.beer/images/languages/english.svg" },
    { id: 2, language: "Svenska", image: "https://cdn.moes.beer/images/languages/swedish.svg" },
  ],
  icons: [
    { icon: undefined },
    { icon: BadgeCheck, color: 'green' },
    { icon: Crown, color: 'gold' },
    { icon: Trash2, color: 'saddlebrown' },
  ],
};

const initialState: RootState = {
  auth: initialAuth,
  ui: initialUi,
  page: initialPage,
  settings: initialSettings,
};

// Variable for custom announcements
let CustomAnnouncement = 1;

function auth_dispatcher(state: AuthState = initialAuth, action: AppAction): AuthState {
  AppUtils.log("[auth_dispatcher]", action, state);
  const data = action.payload;

  switch(action.type) {
    case AppConstants.LOGIN:
      return {
        ...state,
        login: {
          ...state.login,
          isLoading: true,
          error: null,
          loggedIn: false
        }
      };
    case AppConstants.LOGIN_SUCCESS:
      const { token, ...cleanData } = data;
      AppUtils.saveLoginCookie(token);
      return {
        ...state,
        login: {
          ...state.login,
          isLoading: false,
          error: null,
          loggedIn: true
        },
        user: {
          ...state.user,
          ...cleanData
        }
      };
    case AppConstants.LOGIN_FAILURE:
      if(action.error !== "Network Error") {
        AppUtils.removeLoginCookie();
      }
      return {
        ...state,
        login: {
          ...state.login,
          isLoading: false,
          error: action.error || null,
          loggedIn: false
        }
      };
    case AppConstants.LOGOUT_SUCCESS:
      AppUtils.removeLoginCookie();
      return {
        ...state,
        ...initialAuth
      };
    case AppConstants.REGISTER:
      return {
        ...state,
        register: {
          ...state.register,
          isLoading: true,
          error: null,
          isRegistered: false
        }
      };
    case AppConstants.REGISTER_SUCCESS:
      return {
        ...state,
        register: {
          ...state.register,
          isLoading: false,
          error: null,
          isRegistered: true
        },
        user: {
          ...state.user,
          ...data
        }
      };
    case AppConstants.REGISTER_FAILURE:
      return {
        ...state,
        register: {
          ...state.register,
          isLoading: false,
          error: action.error || null,
          isRegistered: false
        }
      };
    case AppConstants.LOGIN_REGISTER_RESET:
      return {
        ...state,
        login: {
          ...state.login,
          error: null
        },
        register: {
          ...state.register,
          error: null
        }
      };
    case AppConstants.UPDATE:
      return {
        ...state,
        update: {
          ...state.update,
          isLoading: true,
          error: null
        }
      };
    case AppConstants.UPDATE_SUCCESS:
      return {
        ...state,
        update: {
          ...state.update,
          isLoading: false,
          error: null
        },
        user: {
          ...state.user,
          ...data
        }
      };
    case AppConstants.UPDATE_FAILURE:
      return {
        ...state,
        update: {
          ...state.update,
          isLoading: false,
          error: action.error || null,
        }
      };
    default:
      return state;
  }
}

function page_dispatcher(state: PageState = initialPage, action: AppAction): PageState {
  const data = action.payload;
  //AppUtils.log("[page_dispatcher]", action, state);

  switch(action.type) {
    case AppConstants.CREATE_LOBBY:
      return {
        ...state,
        create: {
          ...state.create,
          isLoading: true,
          error: null,
          success: false,
          mode: 0,
          code: 0
        }
      };
    case AppConstants.CREATE_LOBBY_SUCCESS:
      if(data.mode === AppConstants.GAMEMODE_SPEAKER) {
        return {
          ...state,
          create: {
            ...state.create,
            isLoading: false,
            error: null,
            success: true,
            mode: data.mode,
            code: data.code
          },
          speaker: {
            ...state.speaker,
            questions: data.questions,
            code: data.code
          },
          lobby: {
            ...state.lobby,
            error: null,
          }
        };
      }
      // Add cookie for other games
      AppUtils.saveCookie(data.code, data.cookie);
      return {
        ...state,
        create: {
          ...state.create,
          isLoading: false,
          error: null,
          success: true,
          mode: data.mode,
          code: data.code
        },
        lobby: {
          ...state.lobby,
          error: null,
        }
      };
    case AppConstants.CREATE_LOBBY_FAILURE:
      return {
        ...state,
        create: {
          ...state.create,
          isLoading: false,
          error: action.error || null,
          success: false,
          mode: 0,
          code: 0
        }
      };
    case AppConstants.CREATE_LOBBY_RESET:
      return {
        ...state,
        create: {
          ...state.create,
          isLoading: false,
          error: null,
          success: false,
          code: 0,
        },
        lobby: {
          ...state.lobby,
          error: null,
        }
      };
    case AppConstants.REMATCH_LOBBY_SUCCESS:
      /* Do nothing, we'll get a LOBBY_UPDATE in a second or two. */
      return state;
    case AppConstants.GAME_START:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          error: null
        }
      };
    case AppConstants.GAME_START_FAILURE:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          error: action.error || null
        }
      };
    case AppConstants.SHOW_QUESTION:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          ...data
        }
      };
    case AppConstants.JOIN_LOBBY:
      return {
        ...state,
        join: {
          ...state.join,
          isLoading: true,
          error: null,
        }
      };
    case AppConstants.JOIN_LOBBY_SUCCESS:
      // Save cookie!
      AppUtils.saveCookie(data.code, data.cookie);
      return {
        ...state,
        join: {
          ...state.join,
          isLoading: false,
          error: null,
          ...data
        },
        lobby: {
          ...state.lobby,
          error: null,
        }
      };
    case AppConstants.JOIN_LOBBY_FAILURE:
      return {
        ...state,
        join: {
          ...state.join,
          isLoading: false,
          error: action.error || null,
        }
      };
    case AppConstants.LOBBY_KICKED:
      if(state.lobby.code === data.code) {
        return {
          ...state,
          lobby: {
            ...state.lobby,
            error: AppConstants.ERR_LOBBY_KICKED
          },
        };
      }
      return state;
    case AppConstants.LEAVE_LOBBY_SUCCESS:
      AppUtils.removeCookie(data.code);
      return {
        ...state,
        lobby: initialLobby,
        join: {
          ...state.join,
          code: 0,
        },
      };
    case AppConstants.JOIN_LOBBY_RESET:
      return {
        ...state,
        join: {
          ...state.join,
          isLoading: false,
          error: null,
          code: 0
        }
      };
    case AppConstants.LOBBY_STATE:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          ...data
        }
      };
    case AppConstants.LOBBY_UPDATE: {
      return {
        ...state,
        lobby: {
          ...state.lobby,
          error: null,
          ...data,
        }
      };
    }
    case AppConstants.LOBBY_ERROR:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          error: action.error || null
        }
      };
    case AppConstants.LOBBY_PLAYER:
      if(data.add) {
        return {
          ...state,
          lobby: {
            ...state.lobby,
            players: [
              ...state.lobby.players,
              { nickname: data.nickname, online: data.online, icon: data.icon }
            ]
          }
        };
      }
      return {
        ...state,
        lobby: {
          ...state.lobby,
          players: state.lobby.players.filter((a) => {
            return (a.nickname !== data.nickname);
          })
        }
      };
    case AppConstants.LOBBY_ADMIN:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          admin: data.admin
        }
      };
    case AppConstants.LOBBY_TIMELEFT:
      if(data.state === state.lobby.state) {
        return {
          ...state,
          lobby: {
            ...state.lobby,
            timeleft: data.timeleft
          }
        };
      }
      return state;
    case AppConstants.LOBBY_RESET:
      return {
        ...state,
        lobby: initialLobby
      };
    case AppConstants.LOBBY_PLAYER_STATUS:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          players: state.lobby.players.map((player) =>
            player.nickname === data.nickname
            ? {
              ...player,
              online: data.online,
              icon: data.icon
            }
            : player
          ),
        },
      };
    case AppConstants.GAME_RESET_ANSWER:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          answer: 0,
          ready: 0,
          ready_status: ''
        }
      };
    case AppConstants.READY_SUCCESS:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          ...data
        }
      };
    case AppConstants.GAME_ANSWER_SUCCESS:
      return {
        ...state,
        lobby: {
          ...state.lobby,
          ...data
        }
      };
    case AppConstants.COOKIE_FAILURE:
      AppUtils.log("Removing cookie", data.code);
      AppUtils.removeCookie(data.code);
      return {
        ...state,
        lobby: {
          ...state.lobby,
          error: action.error || null
        }
      };
    case AppConstants.COOKIE_FAILURE_FORGET:
      AppUtils.log("Removing cookie", data.code);
      AppUtils.removeCookie(data.code);
      return {
        ...state,
        lobby: {
          ...state.lobby,
          error: action.error || null
        }
      };
    case AppConstants.LOBBY_COOKIE:
      /* We got cookie data.cookie for lobby data.code */
      AppUtils.saveCookie(data.lobby, data.cookie);
      AppUtils.log("Cookie should be saved as "+data.lobby+" -> "+data.cookie);
      return state;
    default:
      return state;
  }
}

function ui_dispatcher(state: UiState = initialUi, action: AppAction): UiState {
  const data = action.payload;

  switch(action.type) {
    /* Websocket stuff */
    case AppConstants.WEBSOCKET_STATE:
      AppUtils.log("[Websocket State] Changed from", state.websocketState, "to", data.state);
      return {
        ...state,
        websocketState: data.state,
        websocketLastError: data.lastError
      };

    /* Language */
    case AppConstants.LANGUAGE_SET:
      for(let x in state.languageAvailable) {
        let v = state.languageAvailable[x];
        if(data.language.toLowerCase() === v.short.toLowerCase()) {
          return {
            ...state,
            language: v
          };
        }
      }
      /* No hit? */
      return state;

    /* Announcements */
    case AppConstants.ANNOUNCEMENT_ADD:
      const announcements = state.announcements || [];
      /* Don't re-add announcements with the same ID */
      if(data.id && announcements.length > 0) {
        for(let i = 0; i < announcements.length; i++) {
          if(announcements[i].id === data.id) {
            return state;
          }
        }
      }

      return {
        ...state,
        announcements: [
          ...announcements,
          { id: (typeof data.id === 'undefined' || data.id === 0 ? 'C'+CustomAnnouncement++ : data.id), message: data.message, url: data.url }
        ]
      };
    case AppConstants.ANNOUNCEMENT_CLOSE:
      return {
        ...state,
        announcements: (state.announcements || []).filter((a) => {
          return (a.id !== data.id);
        })
      };
    case AppConstants.COOKIE_VALIDATE_CLEAR:
      return {
        ...state,
        ongoingGames: []
      };
    case AppConstants.COOKIE_VALIDATE_SUCCESS: {
      const validCookies: OngoingGame[] = [];
      data.cookies.forEach((cookie: any) => {
        if (cookie.valid === 1) {
          validCookies.push(cookie);
        } else {
          AppUtils.removeCookie(cookie.lobby);
        }
      });

      return {
        ...state,
        ongoingGames: validCookies
      };
    }
    case AppConstants.COOKIE_VALIDATE_FAILURE:
      return {
        ...state,
        ongoingGames: [...state.ongoingGames]
      };
    case AppConstants.LEAVE_LOBBY_SILENT_SUCCESS:
    case AppConstants.LEAVE_LOBBY_SILENT_FAILURE:
      AppUtils.log("Removing cookie", data.code, "due to", action.type);
      AppUtils.removeCookie(data.code);
      return {
        ...state,
        ongoingGames: state.ongoingGames.filter((a) => {
          return (a.lobby !== data.code);
        })
      };
    default:
      return state;
  }
}

function settings_dispatcher(state: SettingsState = initialSettings, action: AppAction): SettingsState {
  const data = action.payload;

  AppUtils.log("[settings_dispatcher]", action, state);
  switch(action.type) {
    case AppConstants.SETTINGS_LOAD_SUCCESS:
      return {
        ...state,
        ...data
      };
    default:
      return state;
  }
}

const rootReducer = combineReducers({
  auth: auth_dispatcher,
  ui: ui_dispatcher,
  page: page_dispatcher,
  settings: settings_dispatcher
});

const AppStore = createStore(
  rootReducer,
  initialState,
  applyMiddleware(Middleware)
);

export default AppStore;